import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Flex,
  Space,
  Select,
  Card,
  Typography,
  Alert,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { useTranslation } from 'react-i18next';
import { formatJson } from './assetDetail.utils';
import { useAssetDetailComponentColumns } from './useAssetDetailComponentColumns';
import { ComponentTwinsGroupsSection } from './ComponentTwinsGroupsSection/ComponentTwinsGroupsSection';
import { useAssetDetailComponents } from './hooks/useAssetDetailComponents';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type Props = {
  organizationId: string;
  assetId: string;
  components: AssetDetailComponent[];
};

export function AssetDetailComponentsTab({
  organizationId,
  assetId,
  components,
}: Props) {
  const { t } = useTranslation('assets');
  const page = useAssetDetailComponents(organizationId, assetId, components);
  const columns = useAssetDetailComponentColumns({
    onEdit: page.handleEdit,
    onDelete: page.handleDelete,
  });
  const selectedOcppRole = Form.useWatch('componentType', page.form);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ComponentTwinsGroupsSection
        organizationId={organizationId}
        assetId={assetId}
        components={components}
      />

      <Card
        size="small"
        title={t('detail.components.tableTitle', 'Installed components')}
        extra={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={page.search}
            onChange={(event) => page.applySearch(event.target.value)}
            placeholder={t(
              'detail.components.searchPlaceholder',
              'Search by name, serial, or OCPP ID',
            )}
            style={{ width: 280 }}
          />
        }
      >
        {page.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={t(
              'detail.components.loadError',
              'Failed to load components',
            )}
          />
        ) : null}

        <EvimoTable<AssetDetailComponent>
          className="ds-table"
          columns={columns}
          dataSource={page.rows}
          rowKey="id"
          loading={page.isLoading}
          scroll={{ x: 2400 }}
          pagination={{
            current: page.page,
            pageSize: page.pageSize,
            total: page.total,
            pageSizeOptions: page.pageSizeOptions,
            onChange: page.handlePageChange,
          }}
          expandable={{
            rowExpandable: (record) =>
              !!record.attributes && Object.keys(record.attributes).length > 0,
            expandedRowRender: (record) => (
              <div>
                <Typography.Text type="secondary">
                  {t('detail.components.metadataLabel', 'Metadata')}
                </Typography.Text>
                <pre
                  style={{
                    margin: '8px 0 0',
                    padding: 12,
                    borderRadius: 10,
                    background: '#f8fafc',
                    overflowX: 'hidden',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {formatJson(record.attributes)}
                </pre>
              </div>
            ),
          }}
          locale={{
            emptyText: t(
              'detail.components.empty',
              'No components are linked to this asset.',
            ),
          }}
        />
      </Card>

      <Modal
        title={t('detail.components.editModal.title', 'Edit Component')}
        open={!!page.editingComponent}
        onCancel={page.handleCancel}
        destroyOnClose
        footer={
          <Flex gap={8} justify="flex-end">
            <Button onClick={page.handleCancel} disabled={page.isSaving}>
              {t('detail.components.editModal.cancel', 'Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={page.handleSave}
              loading={page.isSaving}
            >
              {t('detail.components.editModal.save', 'Save Changes')}
            </Button>
          </Flex>
        }
        width={720}
      >
        <Form form={page.form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('detail.components.form.name', 'Component Name')}
            rules={[
              {
                required: true,
                message: t(
                  'detail.components.form.nameRequired',
                  'Component name is required',
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'detail.components.form.namePlaceholder',
                'Enter component name',
              )}
            />
          </Form.Item>

          <Form.Item
            name="assetContextId"
            label={t('detail.components.form.assetContext', 'Asset context')}
            tooltip={t(
              'detail.components.form.assetContextHint',
              'Knowledge-base mapping: asset type → subsystem → component class → component type',
            )}
            rules={[
              {
                required: true,
                message: t(
                  'detail.components.form.assetContextRequired',
                  'Asset context is required',
                ),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={page.contextsLoading}
              options={page.contextOptions}
              placeholder={t(
                'detail.components.form.assetContextPlaceholder',
                'Select asset context',
              )}
            />
          </Form.Item>

          <Form.Item
            name="componentType"
            label={t('detail.components.form.ocppRole', 'OCPP role')}
            tooltip={t(
              'detail.components.form.ocppRoleHint',
              'Discriminator: connector or controller',
            )}
          >
            <Select
              allowClear
              options={page.ocppRoleOptions}
              placeholder={t(
                'detail.components.form.ocppRolePlaceholder',
                'Select OCPP role',
              )}
            />
          </Form.Item>

          <Form.Item
            name="connectorId"
            label={t('detail.components.form.connectorId', 'Connector ID')}
            tooltip={t(
              'detail.components.form.connectorIdHint',
              'OCPP connector index on the charge box (1, 2, 3, …)',
            )}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={t(
                'detail.components.form.connectorIdPlaceholder',
                'e.g. 1',
              )}
            />
          </Form.Item>

          <Form.Item
            name="serialNumber"
            label={t('detail.components.form.serialNumber', 'Serial Number')}
          >
            <Input
              placeholder={t(
                'detail.components.form.serialNumberPlaceholder',
                'Enter serial number',
              )}
            />
          </Form.Item>

          <Form.Item
            name="ocppId"
            label={t('detail.components.form.ocppId', 'OCPP ID')}
            tooltip={t(
              'detail.components.form.ocppIdHint',
              'Charge-box identity (ocpp_id). Connectors usually inherit this from their controller.',
            )}
          >
            <Input
              placeholder={t(
                'detail.components.form.ocppIdPlaceholder',
                'Enter OCPP ID',
              )}
              allowClear
            />
          </Form.Item>

          {selectedOcppRole !== 'controller' ? (
            <Form.Item
              name="controllerId"
              label={t('detail.components.form.controller', 'Controller')}
              tooltip={t(
                'detail.components.form.controllerHint',
                'Connectors belong to a controller (charge box) on this asset — same ocpp_id in the import sheet.',
              )}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={page.controllerOptions}
                placeholder={t(
                  'detail.components.form.controllerPlaceholder',
                  'Select controller',
                )}
                onChange={(value) => {
                  const selected = page.controllerOptions.find(
                    (option) => option.value === value,
                  );
                  if (selected?.ocppId) {
                    page.form.setFieldValue('ocppId', selected.ocppId);
                  }
                }}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="installedAt"
            label={t('detail.components.form.installedAt', 'Installed At')}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder={t(
                'detail.components.form.installedAtPlaceholder',
                'Select installation date',
              )}
            />
          </Form.Item>

          <Form.Item
            name="removedAt"
            label={t('detail.components.form.removedAt', 'Removed At')}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder={t(
                'detail.components.form.removedAtPlaceholder',
                'Select removal date',
              )}
            />
          </Form.Item>

          <Form.Item
            name="attributesJson"
            label={t('detail.components.form.attributes', 'Metadata (JSON)')}
            tooltip={t(
              'detail.components.form.attributesHint',
              'Free-form JSON object stored on the component',
            )}
          >
            <Input.TextArea
              rows={6}
              placeholder={t(
                'detail.components.form.attributesPlaceholder',
                '{"channel":"A1"}',
              )}
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
